/* global Shopify */

import React, {useMemo, useState} from "react";
import PropTypes from "prop-types";
import {useMediaBreakpoint} from "../../hooks/useMediaBreakpoint";
import {Modal} from "../../components/Modal";
import {userManager} from "../../managers/UserManager";
import {Picture} from "../../components/Picture";
import {SelfHostedServerStatus} from "./SelfHostedServerStatus";
import {SelfHostedServerExpirationDate} from "./SelfHostedServerExpirationDate";
import {i18nFormat} from "../../lib/i18nFormat";

export function SelfHostedServerTransferLicenseDialog(props) {
  const servers = userManager.getSelfHostedServers()
  const homeySHSImage = "/img/pages/account/self-hosted-servers/homey-self-hosted-server.png";
  const subscriptionWarningImage = "/img/pages/account/self-hosted-servers/subscription-warning.png";
  const {breakpointUp} = useMediaBreakpoint()
  const [step, setStep] = useState("select"); // "select", "confirm", "monthly-active"
  const [selectedServer, setSelectedServer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const availableServers = useMemo(() => {
    return servers.filter((server) => {
      return server.id !== props.server.id && server.selfHostedServerSubscriptionType !== "lifetime"
    });
  }, [servers, props.server.id]);

  function onClose() {
    setStep("select");
    setSelectedServer(null);
    props.onClose();
  }

  function getHeader() {
    switch (step) {
      case "select":
        return (
          <div className="account-shs-transfer-license-dialog__header">
            <h2 className="account-shs-transfer-license-dialog__title">
              {i18nFormat(`shs.transfer-license-dialog.select.title`)}
            </h2>
            <p className="account-shs-transfer-license-dialog__description">
              {i18nFormat(`shs.transfer-license-dialog.select.description`)}
            </p>
          </div>
        );
      case "confirm":
        return (
          <div className="account-shs-transfer-license-dialog__header">
            <h2 className="account-shs-transfer-license-dialog__title">
              {i18nFormat(`shs.transfer-license-dialog.confirm.title`)}
            </h2>
            <p className="account-shs-transfer-license-dialog__description">
              {i18nFormat(`shs.transfer-license-dialog.confirm.description`)}
            </p>
          </div>
        );
      case "monthly-active":
        return (
          <div className="account-shs-transfer-license-dialog__header">
            <h2 className="account-shs-transfer-license-dialog__title">
              {i18nFormat(`shs.transfer-license-dialog.monthly-active.title`)}
            </h2>
            <p className="account-shs-transfer-license-dialog__description">
              {i18nFormat(`shs.transfer-license-dialog.monthly-active.description`)}
            </p>
          </div>
        );
      default:
        return null;
    }
  }

  function getContent() {
    switch (step) {
      case "select":
        return (
          <div className="account-shs-transfer-license-dialog__select-options">
            {availableServers.map((server) => (
              <div key={server.id} className="account-shs-transfer-license-dialog__select-option">
                <Picture className="account-shs-transfer-license-dialog__select-image" src={homeySHSImage}
                         alt="Homey Self-Hosted Server"/>
                <div className="account-shs-transfer-license-dialog__select-info">
                  <h3 className="account-shs-transfer-license-dialog__select-name">{server.name}</h3>
                  <div className="account-shs-transfer-license-dialog__select-details">
                    <SelfHostedServerStatus className="account-shs-transfer-license-dialog__select-status"
                                            server={server}></SelfHostedServerStatus>
                    <SelfHostedServerExpirationDate className="account-shs-transfer-license-dialog__select-expiration"
                                                    server={server}></SelfHostedServerExpirationDate>
                  </div>
                </div>
                <button type="submit"
                        className="account-shs-transfer-license-dialog__select-button gradient-button-blue-tiny"
                        onClick={() => handleSelect(server)}>
                  <span>{i18nFormat(`shs.transfer-license-dialog.select.button`)}</span>
                </button>
              </div>
            ))}
          </div>
        );
      case "confirm":
        return (
          <div className="account-shs-transfer-license-dialog__confirm-comparison">
            <div className="account-shs-transfer-license-dialog__confirm-current">
              <h3
                className="account-shs-transfer-license-dialog__confirm-title">{i18nFormat("shs.transfer-license-dialog.current")}</h3>
              <div className="account-shs-transfer-license-dialog__confirm-server">
                <Picture className="account-shs-transfer-license-dialog__confirm-image" src={homeySHSImage}
                         alt="Homey Self-Hosted Server"/>
                <div className="account-shs-transfer-license-dialog__confirm-info">
                  <h3 className="account-shs-transfer-license-dialog__confirm-name">{props.server.name}</h3>
                  <div className="account-shs-transfer-license-dialog__confirm-details">
                    <SelfHostedServerStatus className="account-shs-transfer-license-dialog__confirm-status"
                                            server={props.server}></SelfHostedServerStatus>
                  </div>
                </div>
              </div>
              <div className="account-shs-transfer-license-dialog__confirm-server">
                <Picture className="account-shs-transfer-license-dialog__confirm-image" src={homeySHSImage}
                         alt="Homey Self-Hosted Server"/>
                <div className="account-shs-transfer-license-dialog__confirm-info">
                  <h3 className="account-shs-transfer-license-dialog__confirm-name">{selectedServer.name}</h3>
                  <div className="account-shs-transfer-license-dialog__confirm-details">
                    <SelfHostedServerStatus className="account-shs-transfer-license-dialog__confirm-status"
                                            server={selectedServer}></SelfHostedServerStatus>
                  </div>
                </div>
              </div>
            </div>
            <div className={`account-shs-transfer-license-dialog__confirm-comparison-icon ${breakpointUp("md") ? "glyph-core-arrow-right" : "glyph-core-arrow-down"}`}/>
            <div className="account-shs-transfer-license-dialog__confirm-new">
              <h3
                className="account-shs-transfer-license-dialog__confirm-title">{i18nFormat("shs.transfer-license-dialog.new")}</h3>
              <div className="account-shs-transfer-license-dialog__confirm-server">
                <Picture className="account-shs-transfer-license-dialog__confirm-image" src={homeySHSImage}
                         alt="Homey Self-Hosted Server"/>
                <div className="account-shs-transfer-license-dialog__confirm-info">
                  <h3 className="account-shs-transfer-license-dialog__confirm-name">{props.server.name}</h3>
                  <div className="account-shs-transfer-license-dialog__confirm-details">
                    <SelfHostedServerStatus className="account-shs-transfer-license-dialog__confirm-status"
                                            status="inactive"></SelfHostedServerStatus>
                  </div>
                </div>
              </div>
              <div className="account-shs-transfer-license-dialog__confirm-server">
                <Picture className="account-shs-transfer-license-dialog__confirm-image" src={homeySHSImage}
                         alt="Homey Self-Hosted Server"/>
                <div className="account-shs-transfer-license-dialog__confirm-info">
                  <h3 className="account-shs-transfer-license-dialog__confirm-name">{selectedServer.name}</h3>
                  <div className="account-shs-transfer-license-dialog__confirm-details">
                    <SelfHostedServerStatus className="account-shs-transfer-license-dialog__confirm-status"
                                            status="lifetime"></SelfHostedServerStatus>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "monthly-active":
        return (
          <div className="account-shs-transfer-license-dialog__monthly-active-content">
            <Picture className="account-shs-transfer-license-dialog__monthly-active-image"
                     src={subscriptionWarningImage}
                     alt="Homey Self-Hosted Server subscription card with warning"/>
          </div>
        );
      default:
        return null;
    }
  }

  function getFooter() {
    switch (step) {
      case "confirm":
        return (
          <div className="account-shs-transfer-license-dialog__footer">
            <button type="button"
                    data-is-loading={isLoading}
                    className="account-shs-transfer-license-dialog__button gradient-button-blue-full"
                    onClick={handleConfirm}
                    disabled={isLoading}>
              {isLoading && (<span className="account-shs-transfer-license-dialog__loading-throbber"/>)}
              <span>{i18nFormat(`shs.transfer-license-dialog.confirm.button`)}</span>
            </button>
          </div>
        );
      case "monthly-active":
        return (
          <div className="account-shs-transfer-license-dialog__footer">
            <button type="button"
                    className="account-shs-transfer-license-dialog__button gradient-button-blue-full"
                    onClick={handleManageSubscription}>
              <span>{i18nFormat(`shs.transfer-license-dialog.monthly-active.button`)}</span>
            </button>
          </div>
        );
      case "select":
      default:
        return null;
    }
  }

  function handleSelect(server) {
    setSelectedServer(server);
    setStep("confirm");
  }

  async function handleConfirm() {
    const monthlyActive = selectedServer.selfHostedServerSubscriptionType === "monthly" && selectedServer.selfHostedServerSubscriptionActive;
    const minLoadingTime = 500; // make sure at least 500ms loading to avoid flicker
    const startTime = Date.now();

    try {
      setIsLoading(true);
      await window.hyWebsite.cloudApi.transferHomeySelfHostedServerLifetimeLicense({
        homeyId: props.server.id,
        newHomeyId: selectedServer.id,
      });

      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minLoadingTime) {
        await new Promise((resolve) => setTimeout(resolve, minLoadingTime - elapsedTime));
      }

      // If the new server has an active monthly subscription, show warning and button to manage subscription.
      if (monthlyActive) {
        setStep("monthly-active");
        // Do not close the dialog yet to allow user to manage subscription.
      } else {
        await window.hyWebsite.cloudApi.refreshUser();
      }
    } catch (error) {
      alert(error)
    } finally {
      setIsLoading(false);
    }
  }

  function handleManageSubscription() {
    const shopify = new Shopify();
    let url = shopify.getAccountSubscriptionUrl();
    if (selectedServer.selfHostedServerSubscriptionShopifyContractId) {
      url = `${url}/subscriptions/${selectedServer.selfHostedServerSubscriptionShopifyContractId.replace('gid://shopify/SubscriptionContract/', '')}`;
    }

    window.open(url);
  }

  function handleClose() {
    // Refresh user data to reflect the transferred license.
    Promise.resolve().then(async () => {
      await window.hyWebsite.cloudApi.refreshUser();
    }).catch(error => alert(error));

    onClose();
  }

  return (
    <Modal isOpen={props.isOpen} onClose={handleClose}>
      <div className="account-shs-transfer-license-dialog">
        <button className="account-shs-transfer-license-dialog__close-button glyph-core-cross"
                onClick={handleClose}>
        </button>
        {getHeader()}
        {getContent()}
        {getFooter()}
      </div>
    </Modal>
  );
}

SelfHostedServerTransferLicenseDialog.propTypes = {
  server: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    selfHostedServerSubscriptionActive: PropTypes.bool.isRequired,
    selfHostedServerSubscriptionType: PropTypes.oneOf(["trial", "lifetime", "monthly"]).isRequired,
    selfHostedServerSubscriptionExpiresAt: PropTypes.string,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};